import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, filter, map, shareReplay, Subscription, switchMap } from 'rxjs';
import { DesignService } from 'src/app/state';
import { EquipmentDetailsService } from 'src/app/state/equipment/equipment-details';
import { EquipmentLookupService } from 'src/app/state/equipment/equipment-lookup.service';

@Injectable({ providedIn: 'root' })
export class EquipmentFacadeService implements OnDestroy {
  private readonly subscriptions = new Subscription();

  public readonly equipmentList$ = this.equipmentLookup.equipmentList$;
  public readonly selectedEquipmentId$ = this.equipmentLookup.selectedEquipment$;
  public readonly equipmentDetails$ = this.equipmentDetails.details$;

  public readonly loading$ = combineLatest([
    this.equipmentLookup.loading$,
    this.equipmentDetails.loading$,
  ]).pipe(
    map(([listLoading, detailsLoading]) => listLoading || detailsLoading),
    shareReplay(1)
  );

  public readonly error$ = combineLatest([
    this.equipmentLookup.error$,
    this.equipmentDetails.error$,
  ]).pipe(
    map(([listError, detailsError]) => listError || detailsError),
    shareReplay(1)
  );

  /**
   * Equipment data is available and there is no loading or error.
   */
  public readonly hasData$ = combineLatest([
    this.equipmentDetails$,
    this.loading$,
    this.error$,
  ]).pipe(
    map(([equipment, loading, error]) => !!equipment && !loading && !error),
    shareReplay(1)
  );

  /**
   * Equipment ID is selected, and request completed with no data, and no error.
   */
  public readonly hasNoData$ = combineLatest([
    this.selectedEquipmentId$,
    this.equipmentDetails$,
    this.loading$,
    this.error$,
  ]).pipe(
    map(([id, equipment, loading, error]) => !!id && !equipment && !loading && !error),
    shareReplay(1)
  );

  /**
   * A real error occurred (non-404), and loading is finished.
   */
  public readonly hasError$ = this.error$.pipe(
    map((error) => !!error),
    shareReplay(1)
  );

  public constructor(
    private readonly designContext: DesignService,
    private readonly equipmentLookup: EquipmentLookupService,
    private readonly equipmentDetails: EquipmentDetailsService
  ) {
    this.subscriptions.add(
      this.designContext.designId$
        .pipe(
          filter((id): id is string => !!id),
          switchMap((designId) => this.equipmentLookup.loadByDesign(designId))
        )
        .subscribe()
    );
  }

  public setSelectedEquipment(id: string): void {
    this.equipmentLookup.setSelectedEquipment(id);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
